// Chemin: C:\gestion-copro\src\pages\admin-saas\GestionDemosPage.tsx
// Description: Page admin SaaS — Gestion des démos commerciales premium
//              Création de comptes démo nominatifs, suivi en temps réel,
//              prolongation, fin manuelle, configuration liens Calendly/inscription
// Session 239 : Nouveau
// Session 241 : 5 statuts (programmee, envoyee, active, terminee, resetee)
//               Champ dateDebut optionnel dans la modal de création
//               Bouton "Envoyer les accès" sur les démos programmées et envoyées
//               dateExpiration optionnelle (absente avant le premier login)
//               Modal résultat adapté selon statut (programmée vs immédiate)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Play,
  Square,
  Clock,
  RefreshCw,
  Settings,
  Trash2,
  ChevronDown,
  ChevronUp,
  Copy,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  User,
  Calendar,
  BarChart2,
  Send,
} from 'lucide-react';
import {
  Demo,
  Scenario,
  ConfigDemo,
  chargerDemos,
  chargerScenarios,
  chargerConfigDemo,
  sauvegarderConfigDemo,
  creerDemo,
  terminerDemo,
  prolongerDemo,
  envoyerAccesDemo,
  calculerTempsRestant,
  libellStatutDemo,
  couleurStatutDemo,
  supprimerDemo,
} from '../../services/demo.service';

// ============================================================================
// TYPES LOCAUX
// ============================================================================

interface CreationForm {
  prospectNom: string;
  prospectEmail: string;
  scenarioId: string;
  dureeMinutes: number;
  quotaAnalyses: number;
  dateDebut: string; // '' = immédiate, sinon datetime-local ISO
}

interface ResultatCreation {
  demoId: string;
  email: string;
  motDePasse: string;
  statut: 'envoyee' | 'programmee';
}

// ============================================================================
// COMPOSANT BADGE STATUT
// ============================================================================

function BadgeStatut({ statut }: { statut: Demo['statut'] }) {
  const couleur = couleurStatutDemo(statut);
  const libelle = libellStatutDemo(statut);
  const classes: Record<string, string> = {
    green:  'bg-green-100 text-green-800',
    blue:   'bg-blue-100 text-blue-800',
    orange: 'bg-orange-100 text-orange-800',
    purple: 'bg-purple-100 text-purple-800',
    gray:   'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classes[couleur] || classes.gray}`}>
      {statut === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse" />}
      {libelle}
    </span>
  );
}

// ============================================================================
// COMPOSANT TIMER TEMPS RESTANT
// ============================================================================

function TimerRestant({ demo }: { demo: Demo }) {
  const [temps, setTemps] = useState(() => calculerTempsRestant(demo));

  useEffect(() => {
    if (demo.statut !== 'active') return;
    const interval = setInterval(() => {
      setTemps(calculerTempsRestant(demo));
    }, 30000);
    return () => clearInterval(interval);
  }, [demo]);

  if (demo.statut !== 'active') return null;
  if (!temps) return <span className="text-red-600 text-sm font-medium">Expirée</span>;

  return (
    <span className="text-sm font-medium text-gray-700">
      {temps.heures > 0 ? `${temps.heures}h ` : ''}{temps.minutes}min restantes
    </span>
  );
}

// ============================================================================
// COMPOSANT CARTE DÉMO
// ============================================================================

interface CarteDemoProps {
  demo: Demo;
  onTerminer: (demo: Demo) => void;
  onProlonger: (demo: Demo) => void;
  onSupprimer: (demo: Demo) => void;
  onEnvoyerAcces: (demo: Demo) => void;
  envoiEnCours: boolean;
}

function CarteDemo({ demo, onTerminer, onProlonger, onSupprimer, onEnvoyerAcces, envoiEnCours }: CarteDemoProps) {
  const [expanded, setExpanded] = useState(false);

  const pourcentageQuota = demo.quotaAnalyses > 0
    ? Math.round((demo.analysesConsommees / demo.quotaAnalyses) * 100)
    : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* En-tête */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 truncate">{demo.prospectNom}</span>
                <BadgeStatut statut={demo.statut} />
              </div>
              <p className="text-sm text-gray-500 truncate">{demo.prospectEmail}</p>
            </div>
          </div>
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Ligne infos rapides */}
        <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {demo.scenarioNom}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <TimerRestant demo={demo} />
            {demo.statut !== 'active' && (
              <span>{demo.dureeMinutes >= 60 ? `${Math.round(demo.dureeMinutes / 60)}h` : `${demo.dureeMinutes}min`}</span>
            )}
          </span>
          <span className="flex items-center gap-1">
            <BarChart2 className="w-3.5 h-3.5" />
            {demo.analysesConsommees}/{demo.quotaAnalyses} analyses
          </span>
        </div>

        {/* Barre quota */}
        <div className="mt-2">
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full ${pourcentageQuota >= 90 ? 'bg-red-500' : pourcentageQuota >= 70 ? 'bg-orange-400' : 'bg-indigo-500'}`}
              style={{ width: `${Math.min(pourcentageQuota, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Détails expandables */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Email démo</p>
              <p className="font-mono text-gray-800 text-xs">{demo.emailDemo}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Créée le</p>
              <p className="text-gray-800">{demo.dateCreation.toLocaleDateString('fr-FR')}</p>
            </div>

            {/* Date programmée — visible uniquement si présente */}
            {demo.dateProgrammee && (
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Date programmée</p>
                <p className="text-gray-800">
                  {demo.dateProgrammee.toLocaleDateString('fr-FR')} à {demo.dateProgrammee.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )}

            {/* Date envoi accès */}
            {demo.dateEnvoiAcces && (
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Accès envoyés le</p>
                <p className="text-gray-800">{demo.dateEnvoiAcces.toLocaleDateString('fr-FR')}</p>
              </div>
            )}

            {/* Premier login */}
            {demo.datePremierLogin && (
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Premier login</p>
                <p className="text-gray-800">
                  {demo.datePremierLogin.toLocaleDateString('fr-FR')} à {demo.datePremierLogin.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )}

            {/* Expiration — absente avant le premier login */}
            {demo.dateExpiration ? (
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Expiration</p>
                <p className="text-gray-800">
                  {demo.dateExpiration.toLocaleDateString('fr-FR')} à {demo.dateExpiration.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ) : (demo.statut === 'active' || demo.statut === 'envoyee') ? (
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Expiration</p>
                <p className="text-gray-500 italic text-xs">Démarre au premier login ({demo.dureeMinutes >= 60 ? `${Math.round(demo.dureeMinutes / 60)}h` : `${demo.dureeMinutes}min`})</p>
              </div>
            ) : null}

            {demo.dateTerminaison && (
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wide mb-0.5">Terminée le</p>
                <p className="text-gray-800">{demo.dateTerminaison.toLocaleDateString('fr-FR')}</p>
              </div>
            )}
          </div>

          {demo.briefSentinelle && (
            <div>
              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Brief SENTINELLE</p>
              <p className="text-gray-700 text-sm italic leading-relaxed">{demo.briefSentinelle}</p>
            </div>
          )}

          {/* Actions selon statut */}
          <div className="flex flex-wrap gap-2 pt-1">
            {/* Envoyer les accès — programmée ou re-envoi depuis envoyee */}
            {(demo.statut === 'programmee' || demo.statut === 'envoyee') && (
              <button
                onClick={() => onEnvoyerAcces(demo)}
                disabled={envoiEnCours}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
              >
                {envoiEnCours ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {demo.statut === 'programmee' ? 'Envoyer les accès' : 'Renvoyer les accès'}
              </button>
            )}

            {/* Prolonger + Terminer — active uniquement */}
            {demo.statut === 'active' && (
              <>
                <button
                  onClick={() => onProlonger(demo)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Prolonger
                </button>
                <button
                  onClick={() => onTerminer(demo)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 text-red-700 font-medium"
                >
                  <Square className="w-3.5 h-3.5" />
                  Terminer
                </button>
              </>
            )}
          </div>

          {/* Supprimer — toujours disponible */}
          <div className="pt-1 border-t border-gray-100 mt-1">
            <button
              onClick={() => onSupprimer(demo)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MODAL CRÉATION DÉMO
// ============================================================================

interface ModalCreationProps {
  scenarios: Scenario[];
  onCreer: (params: CreationForm) => Promise<void>;
  onFermer: () => void;
  enCours: boolean;
}

function ModalCreation({ scenarios, onCreer, onFermer, enCours }: ModalCreationProps) {
  const [form, setForm] = useState<CreationForm>({
    prospectNom: '',
    prospectEmail: '',
    scenarioId: scenarios[0]?.id || '',
    dureeMinutes: 60,
    quotaAnalyses: 10,
    dateDebut: '',
  });

  const valide = form.prospectNom.trim() && form.prospectEmail.trim() && form.scenarioId;

  const estProgrammee = form.dateDebut
    ? new Date(form.dateDebut).getTime() > Date.now()
    : false;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Créer une démo</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {estProgrammee
              ? 'Le compte sera créé maintenant, les accès envoyés manuellement le jour J.'
              : 'Un compte nominatif sera créé et les identifiants envoyés au prospect immédiatement.'}
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du prospect *</label>
            <input
              type="text"
              value={form.prospectNom}
              onChange={e => setForm(f => ({ ...f, prospectNom: e.target.value }))}
              placeholder="Ex : ARC Île-de-France"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email du prospect *</label>
            <input
              type="email"
              value={form.prospectEmail}
              onChange={e => setForm(f => ({ ...f, prospectEmail: e.target.value }))}
              placeholder="contact@arc-idf.fr"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">Les identifiants de connexion seront envoyés à cette adresse.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scénario *</label>
            <select
              value={form.scenarioId}
              onChange={e => setForm(f => ({ ...f, scenarioId: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {scenarios.length === 0 && <option value="">Aucun scénario disponible</option>}
              {scenarios.map(s => (
                <option key={s.id} value={s.id}>{s.nom}</option>
              ))}
            </select>
            {form.scenarioId && scenarios.find(s => s.id === form.scenarioId) && (
              <p className="text-xs text-gray-500 mt-1 italic">
                {scenarios.find(s => s.id === form.scenarioId)?.briefSentinelle?.substring(0, 120)}...
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durée (minutes) *</label>
              <input
                type="number"
                min={5}
                max={1440}
                value={form.dureeMinutes}
                onChange={e => setForm(f => ({ ...f, dureeMinutes: parseInt(e.target.value) || 60 }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {form.dureeMinutes >= 60 ? `${Math.round(form.dureeMinutes / 60 * 10) / 10}h` : `${form.dureeMinutes}min`}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quota analyses *</label>
              <input
                type="number"
                min={1}
                max={100}
                value={form.quotaAnalyses}
                onChange={e => setForm(f => ({ ...f, quotaAnalyses: parseInt(e.target.value) || 10 }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Date de début optionnelle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de démo <span className="text-gray-400 font-normal">(optionnel — laisser vide pour démo immédiate)</span>
            </label>
            <input
              type="datetime-local"
              value={form.dateDebut}
              onChange={e => setForm(f => ({ ...f, dateDebut: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {estProgrammee && (
              <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Démo programmée — vous devrez cliquer sur "Envoyer les accès" le jour J.
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button
            onClick={onFermer}
            disabled={enCours}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={() => onCreer(form)}
            disabled={!valide || enCours}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
          >
            {enCours ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {enCours ? 'Création en cours...' : estProgrammee ? 'Programmer la démo' : 'Lancer la démo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MODAL RÉSULTAT CRÉATION
// ============================================================================

interface ModalResultatProps {
  resultat: ResultatCreation;
  onFermer: () => void;
}

function ModalResultat({ resultat, onFermer }: ModalResultatProps) {
  const [copieEmail, setCopieEmail] = useState(false);
  const [copieMdp, setCopieMdp] = useState(false);

  const copier = (texte: string, setter: (v: boolean) => void) => {
    navigator.clipboard.writeText(texte);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const estProgrammee = resultat.statut === 'programmee';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-6 py-5 text-center border-b border-gray-200">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {estProgrammee ? 'Démo programmée' : 'Démo créée avec succès'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {estProgrammee
              ? 'Le compte est prêt. Cliquez sur "Envoyer les accès" depuis la carte démo le jour J.'
              : 'Les identifiants ont été envoyés au prospect par email.'}
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-gray-600 font-medium">Identifiants de connexion :</p>

          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs text-gray-500">Adresse e-mail</p>
                <p className="font-mono text-sm text-gray-900">{resultat.email}</p>
              </div>
              <button
                onClick={() => copier(resultat.email, setCopieEmail)}
                className="text-gray-400 hover:text-indigo-600 shrink-0"
                title="Copier"
              >
                {copieEmail ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            <div className="border-t border-gray-200 pt-2 flex items-center justify-between gap-2">
              <div>
                <p className="text-xs text-gray-500">Mot de passe</p>
                <p className="font-mono text-sm text-gray-900 tracking-wider">{resultat.motDePasse}</p>
              </div>
              <button
                onClick={() => copier(resultat.motDePasse, setCopieMdp)}
                className="text-gray-400 hover:text-indigo-600 shrink-0"
                title="Copier"
              >
                {copieMdp ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Conservez ces identifiants — ils ne seront plus affichés.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onFermer}
            className="w-full px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MODAL PROLONGATION
// ============================================================================

interface ModalProlongationProps {
  demo: Demo;
  onProlonger: (minutes: number) => Promise<void>;
  onFermer: () => void;
  enCours: boolean;
}

function ModalProlongation({ demo, onProlonger, onFermer, enCours }: ModalProlongationProps) {
  const [minutes, setMinutes] = useState(30);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Prolonger la démo</h2>
          <p className="text-sm text-gray-500 mt-0.5">{demo.prospectNom}</p>
          {!demo.dateExpiration && (
            <p className="text-xs text-orange-600 mt-1">
              Le prospect ne s'est pas encore connecté — la durée totale sera ajustée.
            </p>
          )}
        </div>

        <div className="px-6 py-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Minutes supplémentaires</label>
          <div className="flex gap-2 mb-3">
            {[15, 30, 60, 120].map(m => (
              <button
                key={m}
                onClick={() => setMinutes(m)}
                className={`px-3 py-1.5 text-sm rounded-lg border font-medium ${minutes === m ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                {m >= 60 ? `${m / 60}h` : `${m}min`}
              </button>
            ))}
          </div>
          <input
            type="number"
            min={1}
            max={1440}
            value={minutes}
            onChange={e => setMinutes(parseInt(e.target.value) || 30)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button onClick={onFermer} disabled={enCours} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            Annuler
          </button>
          <button
            onClick={() => onProlonger(minutes)}
            disabled={enCours}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
          >
            {enCours ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
            Prolonger
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SECTION CONFIGURATION
// ============================================================================

interface SectionConfigProps {
  config: ConfigDemo;
  onSauvegarder: (config: ConfigDemo) => Promise<void>;
}

function SectionConfig({ config, onSauvegarder }: SectionConfigProps) {
  const [form, setForm] = useState<ConfigDemo>(config);
  const [enCours, setEnCours] = useState(false);
  const [sauvegarde, setSauvegarde] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => { setForm(config); }, [config]);

  const handleSauvegarder = async () => {
    setEnCours(true);
    try {
      await onSauvegarder(form);
      setSauvegarde(true);
      setTimeout(() => setSauvegarde(false), 2000);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-800">Configuration</span>
          {(!config.lienCalendly || !config.lienInscription) && (
            <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
              <AlertCircle className="w-3 h-3" /> Liens manquants
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien Calendly
              {form.lienCalendly && (
                <a href={form.lienCalendly} target="_blank" rel="noopener noreferrer" className="ml-2 text-indigo-600 hover:underline text-xs inline-flex items-center gap-0.5">
                  <ExternalLink className="w-3 h-3" /> Tester
                </a>
              )}
            </label>
            <input
              type="url"
              value={form.lienCalendly}
              onChange={e => setForm((f: ConfigDemo) => ({ ...f, lienCalendly: e.target.value }))}
              placeholder="https://calendly.com/dominique-sergent62/30min"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">Affiché dans l'email de fin de démo et le message SENTINELLE.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lien inscription
              {form.lienInscription && (
                <a href={form.lienInscription} target="_blank" rel="noopener noreferrer" className="ml-2 text-indigo-600 hover:underline text-xs inline-flex items-center gap-0.5">
                  <ExternalLink className="w-3 h-3" /> Tester
                </a>
              )}
            </label>
            <input
              type="url"
              value={form.lienInscription}
              onChange={e => setForm((f: ConfigDemo) => ({ ...f, lienInscription: e.target.value }))}
              placeholder="https://copro-protect.fr/inscription"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">URL de la page d'inscription fondateur VIGIE.</p>
          </div>

          <button
            onClick={handleSauvegarder}
            disabled={enCours}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
          >
            {enCours ? <RefreshCw className="w-4 h-4 animate-spin" /> : sauvegarde ? <CheckCircle className="w-4 h-4" /> : null}
            {sauvegarde ? 'Sauvegardé' : 'Sauvegarder'}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PAGE PRINCIPALE
// ============================================================================

export default function GestionDemosPage() {
  const [demos, setDemos] = useState<Demo[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [config, setConfig] = useState<ConfigDemo>({ lienCalendly: '', lienInscription: '' });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  // Modals
  const [modalCreation, setModalCreation] = useState(false);
  const [creationEnCours, setCreationEnCours] = useState(false);
  const [resultatCreation, setResultatCreation] = useState<ResultatCreation | null>(null);
  const [demoAProlonger, setDemoAProlonger] = useState<Demo | null>(null);
  const [prolongationEnCours, setProlongationEnCours] = useState(false);
  const [demoATerminer, setDemoATerminer] = useState<Demo | null>(null);
  const [terminaisonEnCours, setTerminaisonEnCours] = useState(false);
  const [demoASupprimer, setDemoASupprimer] = useState<Demo | null>(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const charger = useCallback(async () => {
    try {
      const [d, s, c] = await Promise.all([
        chargerDemos(),
        chargerScenarios(),
        chargerConfigDemo(),
      ]);
      setDemos(d);
      setScenarios(s);
      setConfig(c);
    } catch (err) {
      setErreur('Erreur lors du chargement des données.');
      console.error(err);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Création
  const handleCreer = async (params: CreationForm) => {
    setCreationEnCours(true);
    setErreur('');
    try {
      const result = await creerDemo({
        prospectNom: params.prospectNom,
        prospectEmail: params.prospectEmail,
        scenarioId: params.scenarioId,
        dureeMinutes: params.dureeMinutes,
        quotaAnalyses: params.quotaAnalyses,
        dateDebut: params.dateDebut || undefined,
      });
      setModalCreation(false);
      setResultatCreation({
        demoId: result.demoId,
        email: result.email,
        motDePasse: result.motDePasse,
        statut: result.statut,
      });
      await charger();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErreur(msg || 'Erreur lors de la création de la démo.');
    } finally {
      setCreationEnCours(false);
    }
  };

  // Envoi des accès (démo programmée ou re-envoi)
  const handleEnvoyerAcces = async (demo: Demo) => {
    setEnvoiEnCours(true);
    setErreur('');
    try {
      await envoyerAccesDemo(demo.id);
      await charger();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErreur(msg || "Erreur lors de l'envoi des accès.");
    } finally {
      setEnvoiEnCours(false);
    }
  };

  // Terminaison
  const handleTerminer = async () => {
    if (!demoATerminer) return;
    setTerminaisonEnCours(true);
    setErreur('');
    try {
      await terminerDemo(demoATerminer.id);
      setDemoATerminer(null);
      await charger();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErreur(msg || 'Erreur lors de la terminaison de la démo.');
    } finally {
      setTerminaisonEnCours(false);
    }
  };

  // Suppression
  const handleSupprimer = async () => {
    if (!demoASupprimer) return;
    setSuppressionEnCours(true);
    setErreur('');
    try {
      await supprimerDemo(demoASupprimer.id);
      setDemoASupprimer(null);
      await charger();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErreur(msg || 'Erreur lors de la suppression de la démo.');
    } finally {
      setSuppressionEnCours(false);
    }
  };

  // Prolongation
  const handleProlonger = async (minutes: number) => {
    if (!demoAProlonger) return;
    setProlongationEnCours(true);
    setErreur('');
    try {
      await prolongerDemo(demoAProlonger.id, minutes);
      setDemoAProlonger(null);
      await charger();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErreur(msg || 'Erreur lors de la prolongation de la démo.');
    } finally {
      setProlongationEnCours(false);
    }
  };

  // Regroupement des démos par statut
  const demosEnCours = demos.filter(d => d.statut === 'programmee' || d.statut === 'envoyee' || d.statut === 'active');
  const demosTerminees = demos.filter(d => d.statut === 'terminee' || d.statut === 'resetee');
  const demosActives = demos.filter(d => d.statut === 'active');

  if (chargement) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gestion des démos</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {demosActives.length} active{demosActives.length !== 1 ? 's' : ''} — {demos.length} au total
          </p>
        </div>
        <button
          onClick={() => setModalCreation(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Nouvelle démo
        </button>
      </div>

      {/* Erreur globale */}
      {erreur && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{erreur}</span>
        </div>
      )}

      {/* Configuration */}
      <SectionConfig config={config} onSauvegarder={async (c) => { await sauvegarderConfigDemo(c); setConfig(c); }} />

      {/* Démos en cours (programmées + envoyées + actives) */}
      {demosEnCours.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            En cours ({demosEnCours.length})
          </h2>
          <div className="space-y-3">
            {demosEnCours.map(demo => (
              <CarteDemo
                key={demo.id}
                demo={demo}
                onTerminer={setDemoATerminer}
                onProlonger={setDemoAProlonger}
                onSupprimer={setDemoASupprimer}
                onEnvoyerAcces={handleEnvoyerAcces}
                envoiEnCours={envoiEnCours}
              />
            ))}
          </div>
        </div>
      )}

      {/* Historique */}
      {demosTerminees.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Historique ({demosTerminees.length})
          </h2>
          <div className="space-y-3">
            {demosTerminees.map(demo => (
              <CarteDemo
                key={demo.id}
                demo={demo}
                onTerminer={setDemoATerminer}
                onProlonger={setDemoAProlonger}
                onSupprimer={setDemoASupprimer}
                onEnvoyerAcces={handleEnvoyerAcces}
                envoiEnCours={envoiEnCours}
              />
            ))}
          </div>
        </div>
      )}

      {/* État vide */}
      {demos.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Play className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-base font-medium text-gray-500">Aucune démo créée</p>
          <p className="text-sm mt-1">Cliquez sur "Nouvelle démo" pour commencer.</p>
        </div>
      )}

      {/* Modal création */}
      {modalCreation && (
        <ModalCreation
          scenarios={scenarios}
          onCreer={handleCreer}
          onFermer={() => setModalCreation(false)}
          enCours={creationEnCours}
        />
      )}

      {/* Modal résultat création */}
      {resultatCreation && (
        <ModalResultat
          resultat={resultatCreation}
          onFermer={() => setResultatCreation(null)}
        />
      )}

      {/* Modal prolongation */}
      {demoAProlonger && (
        <ModalProlongation
          demo={demoAProlonger}
          onProlonger={handleProlonger}
          onFermer={() => setDemoAProlonger(null)}
          enCours={prolongationEnCours}
        />
      )}

      {/* Confirmation suppression */}
      {demoASupprimer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Supprimer la démo ?</h2>
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">{demoASupprimer.prospectNom}</span>
            </p>
            <p className="text-sm text-gray-500 mb-5">
              Cette action est irréversible. Le compte Firebase Auth et toutes les données associées seront supprimés.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDemoASupprimer(null)}
                disabled={suppressionEnCours}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSupprimer}
                disabled={suppressionEnCours}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {suppressionEnCours ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation terminaison */}
      {demoATerminer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Terminer la démo ?</h2>
            <p className="text-sm text-gray-600 mb-1">
              <span className="font-medium">{demoATerminer.prospectNom}</span>
            </p>
            <p className="text-sm text-gray-500 mb-5">
              Le compte sera désactivé et un email sera envoyé au prospect avec les liens de suivi.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDemoATerminer(null)}
                disabled={terminaisonEnCours}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleTerminer}
                disabled={terminaisonEnCours}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {terminaisonEnCours ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                Terminer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
